import moment from 'moment';


export const convertEvent = (event, me) => {
  const eevent = event.node ? event.node : event;
  /*
  * find if the current user exist in the required/optional attendees
  * check the responseType and add it to the event object.
  */
  let foundInAttendees;
  if (me) {
    foundInAttendees = eevent.requiredAttendees.find(attendees => attendees.displayName === me.displayName);
    if (!foundInAttendees) {
      foundInAttendees = eevent.optionalAttendees.find(attendees => attendees.displayName === me.displayName);
    }
  }
  return ({
    clientSideObject: true,
    start: moment.unix(eevent.startDate),
    end: moment.unix(eevent.endDate),
    title: eevent.subject,
    location: eevent.location,
    required: eevent.requiredAttendees.map(n => n.displayName),
    optional: eevent.optionalAttendees.map(n => n.displayName),
    chair: eevent.organizer.displayName,
    allDay: eevent.isAllDay,
    occupancy: eevent.occupancy,
    body: eevent.body,
    iCalUId: eevent.iCalUId,
    responseType: typeof foundInAttendees === 'undefined' ? '' : foundInAttendees.responseType,
  });
};

export function mapContactsSelectedToSchedulerContacts(contacts, meetingsRooms, otherResources) {
  // If relay has not finished fetching data or data is pending then
  // viewer.contacts is empty (undefined). In this case no contacts are
  // availible so return an empty list
  if (!contacts || !meetingsRooms || !otherResources) { return []; }

  // Now do the conversion from the objects retrieved from relay to the ones we
  // want in the client. NB this whole datastructure is mutable so we need to
  // check if the given contact and the given event has already been processed.
  // Therefore we check for events.edges and clientSideObject === true.
  const mixedContacts = [...contacts.edges, ...meetingsRooms.edges, ...otherResources.edges];
  let schedulerContacts = mixedContacts ? (
      mixedContacts.map(c => c.node)
    ) : contacts;
  schedulerContacts = schedulerContacts.map(c => {
    const contactCopy = c;
    contactCopy.value = contactCopy.email;
    contactCopy.label = contactCopy.displayName;
    const events = contactCopy.events.edges ? (
      contactCopy.events.edges.map(e => e.node)
    ) : (
      contactCopy.events
    );
    contactCopy.events = events.map(e => {
      const clientSideEvent = e.clientSideObject ? e : convertEvent(e);
      return clientSideEvent;
    });
    return contactCopy;
  });
  return schedulerContacts;
}

export function mapContactsSelectedToSchedulerContactsClone(contacts) {
  // If relay has not finished fetching data or data is pending then
  // viewer.contacts is empty (undefined). In this case no contacts are
  // availible so return an empty list
  if (!contacts) { return []; }

  // Now do the conversion from the objects retrieved from relay to the ones we
  // want in the client. NB this whole datastructure is mutable so we need to
  // check if the given contact and the given event has already been processed.
  // Therefore we check for events.edges and clientSideObject === true.
  const mixedContacts = contacts.edges;
  let schedulerContacts = mixedContacts ? (
      mixedContacts.map(c => c.node)
    ) : contacts;
  schedulerContacts = schedulerContacts.map(c => {
    const contactCopy = c;
    contactCopy.value = contactCopy.email;
    contactCopy.label = contactCopy.displayName;
    const events = contactCopy.events.edges ? (
      contactCopy.events.edges.map(e => e.node)
    ) : (
      contactCopy.events
    );
    contactCopy.events = events.map(e => {
      const clientSideEvent = e.clientSideObject ? e : convertEvent(e);
      return clientSideEvent;
    });
    return contactCopy;
  });
  return schedulerContacts;
}

function extractMeetingroomsFromViewer(viewer: Object) : Array<Object> {
  const meetingRooms = viewer.meetingRooms.edges.map(m => m.node);
  return meetingRooms;
}

export function extractEvent(e: Object) : Object {
  return {
    meetingRoom: e.meetingRoom,
    start: moment.unix(e.node.startDate),
    end: moment.unix(e.node.endDate),
    title: e.node.subject,
    location: e.node.location,
    organizer: e.node.organizer,
    id: e.node.id,
    iCalUId: e.node.iCalUId,
    occupancy: e.node.occupancy,
    requiredAttendees: e.node.requiredAttendees,
    optionalAttendees: e.node.optionalAttendees,
    categories: e.node.categories,
  };
}

export function extractEventsFromEdges(eventEdges: Array<Object>) : Array<Object> {
  // Now build the events list into a form useful for the display component.
  // Does this slice() only does a shallow copy? So it is probably not doing
  // anything useful.
  let events = eventEdges.slice();
  events = events.map(e => extractEvent(e));
  // And sort by start time
  events = events.sort((a, b) => a.start.diff(b.start));
  return events;
}

/* The API does not provide an endpoint for events but it does for all the
 * meeting rooms. This function extracts an array for events from the meeting
 * rooms and puts them into a suitable shape for our MeetingroomOverview
 * Component to consume.
 * */
export function extractEventsFromViewer(viewer: Object) : Array<Object> {
  const meetingRooms = extractMeetingroomsFromViewer(viewer);
  // Extract the events from each meeting room and place in a single list
  let events = [];
  let newEvents = [];

  meetingRooms.forEach(m => {
    newEvents = m.events.edges;
    // Add the name of the meetingRoom to the event. NB this is not perfect as
    // it adds it to the event in the relay cache - however the operation in
    // indempotent so this shouldn't be a problem - but we do need to turn off
    // the linting warning.
    // eslint-disable-next-line no-param-reassign
    newEvents.map(e => { e.meetingRoom = m.displayName; return e; });
    events = events.concat(m.events.edges);
  });
  return extractEventsFromEdges(events);
}

export function filterNotNullEdgeNodes(queryResponse: any): any[] {
  const edges: any[] = queryResponse.edges || [];

  const result = [];
  for (const oneEdge of edges) {
    if (oneEdge != null && oneEdge.node != null) {
      result.push(oneEdge.node);
    }
  }

  return result;
}

export function removePastEvents(events: Array<Object>, now: Object): Array<Object> {
  let upcomingEvents = [];
  events.forEach(e => {
    if (e.end.isSameOrAfter(now)) {
      upcomingEvents = upcomingEvents.concat(e);
    }
  });
  return upcomingEvents;
}

/*
 * getEventStatus returns the current and following event
 * according to the current date.
 * */
export function getEventStatus(events: Array<Object>, now: Object): Object {
  const status = {
    current: null,
    next: null,
  };

  const upcomingEvents = removePastEvents(events, now);
  if (!upcomingEvents.length) {
    return status;
  }

  const e = upcomingEvents[0];
  if (e.start.isSameOrBefore(now)) {
    status.current = e;
    status.next = upcomingEvents.length > 1 ? upcomingEvents[1] : null;
  } else {
    status.next = e;
  }
  return status;
}



// WEBPACK FOOTER //
// ./src/js/libs/Extract.js